import Streams from './streams.js';
import Fetch from './fetch.js';
import MdoRecorder from './mdo-recording.js';

// TRANSCRIPTION CODE — the recorder (mdo-recording.js) is bundled with the main SDK
// and imported statically. A recorder instance is created lazily on the first
// startRecordingAudio() call (see #mdoRecorder below).

class ChannelComms {
    #channelId;
    #streams;
    #mdoRecorder = null;           // active recorder instance, created lazily on first record
    #onTranscriptionState = null;  // optional state callback set by the Bridge
    #clientConnected;

    constructor(channelId, sfu_url, memberPubId, accessCode, bridge) {
        this.#channelId = channelId;
        this.#streams = new Streams(bridge);
        this.#streams.setupServer(channelId, sfu_url, memberPubId, accessCode);
        // TRANSCRIPTION CODE - recorder is created lazily on first startRecordingAudio()
        this.#clientConnected = false;
    }
    
    /* Makedo basic server communication functions */

    // SERVER CONNECT AND EXIT FUNCTIONS
    async joinServer() {
        this.#streams.joinServer();
        this.#clientConnected = true;
    }
    leaveServer(killLocalStreams = true) {
        // killStreams: true = full exit (stop tracks), false = pause (keep streams for preview)
        let status = killLocalStreams ? "steppedout" : "lobby_mid";  // EXIT vs PAUSE
        if (killLocalStreams) {
            // Full exit - kill local streams and upstream
            this.#streams.endFullStreamByType("camera");
            this.#streams.endFullStreamByType("screenshare");
        } else {
            // Pause - close upstream to Galene but keep local streams alive
            this.#streams.closeUpstreamOnly("camera");
            this.#streams.closeUpstreamOnly("screenshare");
        }
        this.#streams.leaveServer(status); 
        this.#clientConnected = false;
    }
    // BASIC getter/setters for [camera, movie, screenshare, microphone] for specified channel
    /*
    inChannelSetCameraVideoWithStream() { //existingStream) {
        if (this.#clientConnected) {this.#streams.setCameraVideoWithStream();}
    }
        */
    inChannelSetStreamVideoTrack(type,onOff,deviceId) {
        if (this.#clientConnected) {this.#streams.setStreamVideoTrack(type, onOff, deviceId);}
    }
    inChannelSetStreamAudioTrack(type,onOff,deviceId) {
        if (this.#clientConnected) {this.#streams.setStreamAudioTrack(type, onOff, deviceId);}
    }
    inChannelSetStreamBothTracks(type, onOff, deviceId) {
        if (this.#clientConnected) {this.#streams.setStreamBothTracks(type, onOff, deviceId);}
    }
    inChannelExitCamera() {
        if (this.#clientConnected) {this.#streams.exitCamera();}
    }

    inChannelGetStreamStates() { return this.#streams.getStreamStates();}

    async inChannelSetMutedForUpStream(streamType, trackType, isMuted) {
        if (!this.#clientConnected) {return false;}
        await this.#streams.setMutedForUpStream(streamType, trackType, isMuted);
    }

    // NEW STREAMS API FUNCTIONS
    async startMedia(type, audioOn, videoOn, deviceId) { return this.#streams.startMedia(type, audioOn, videoOn, deviceId); }
    getMedia(type) { return this.#streams.getMedia(type); }
    getMyUpstream(type) { return this.#streams.getMyUpstream(type); }
    /** Enumerate active peer connections — used by optional connection-quality monitor. */
    getAllActivePeerConnections() { return this.#streams.getAllActivePeerConnections(); }
    toggleStreamByType(type, forceVal) { return this.#streams.toggleStreamByType(type, forceVal); }
    broadcastLocalStream(type) { return this.#streams.broadcastLocalStream(type); }
    endFullStreamByType(type) { return this.#streams.endFullStreamByType(type); }

    // Custom labeled upstreams + subscription + discovery (arbitrary-label extension)
    async publishStream(label, opts) { return this.#streams.publishStream(label, opts); }
    unpublishStream(label) { return this.#streams.unpublishStream(label); }
    subscribeToStream(memberId, label, kinds) { return this.#streams.subscribeToStream(memberId, label, kinds); }
    unsubscribeFromStream(memberId, label) { return this.#streams.unsubscribeFromStream(memberId, label); }
    setDefaultSubscription(map) { return this.#streams.setDefaultSubscription(map); }
    getMemberStreamLabels(memberId) { return this.#streams.getMemberStreamLabels(memberId); }
    exitVideoTrack(type) { return this.#streams.exitVideoTrack(type); }
    exitAudioTrack(type) { return this.#streams.exitAudioTrack(type); }
    async replaceVideoTrack(type, deviceId) { return this.#streams.local_replaceVideoTrack(type, deviceId); }
    async replaceAudioTrack(type, deviceId) { return this.#streams.local_replaceAudioTrack(type, deviceId); }

    // Callback function setters (now initialized from function in video_dom.js)
    setOnSystemMessageToUser(callback) {this.#streams.setOnSystemMessageToUser(callback);}
    
    setOnSelfJoined(callback) {this.#streams.setOnSelfJoined(callback);}
    setOnSelfLive(callback) {this.#streams.setOnNewUpstream((streamId, memberId, type, stream)=>{this.fullNewUpstreamCallback(this, callback, streamId, memberId, type, stream);});}
    setOnSelfExited(callback) { this.#streams.setOnSelfExited((json)=>{this.fullExitCallback(this, callback, json);});}

    setOnNewDownstream(callback) {this.#streams.setOnNewDownstream((streamId, memberId, type, stream)=>{this.fullNewDownstreamCallback(this, callback, streamId, memberId, type, stream);});}
    setOnEndDownstream(callback) {this.#streams.setOnEndDownstream(callback);}
    setOnEndDownTrack(callback) {this.#streams.setOnEndDownTrack(callback);}
    setOnChatMessage(callback) {this.#streams.setOnChatMessage(callback);}

    // TRANSCRIPTION CODE — server-driven recording baton (start/stop arrive from the Bridge)
    /** Lets the Bridge observe recorder lifecycle states for the SDK event surface. */
    setOnTranscriptionState(callback) { this.#onTranscriptionState = callback; }

    async startRecordingAudio() {
        try {
            if (!this.#mdoRecorder) {
                this.#mdoRecorder = new MdoRecorder(this.#channelId, {
                    upload: (blob, meta) => Fetch.uploadMedia({ type: 'audio', blob, meta }),
                    onState: (state) => { this.#onTranscriptionState?.(this.#channelId, state); },
                });
            }
            const tracks = this.getAllCameraAudioTracks() || [];
            await this.#mdoRecorder.start(tracks);
        } catch (e) {
            console.error('startRecordingAudio failed:', e);
        }
    }

    async stopRecordingAudio() {
        try {
            if (this.#mdoRecorder) {
                await this.#mdoRecorder.stop();
                this.#mdoRecorder = null; // fresh recorder per recording segment
            }
        } catch (e) {
            console.error('stopRecordingAudio failed:', e);
        }
    }

    getAllCameraAudioTracks() {
        return this.#streams.getAllCameraAudioTracks();
    }
    // combine recording callbacks with client registered callbacks
    fullNewDownstreamCallback(self, clientsNewDownstreamCallback, streamId, memberId, streamType, stream) {

        // TRANSCRIPTION CODE - if new downstream is camera or microphone, add to the active recorder
        if (stream && (streamType==="camera" || streamType==="microphone")) {
            try {
                const recorder=self.#mdoRecorder;
                const oneAudioTrack=stream.getAudioTracks()[0];
                if (oneAudioTrack && recorder!=null) {recorder.addTrack(oneAudioTrack);}
            } catch(e) {console.error("fullNewDownstreamCallback: Error adding audio track to recorder:"+e);}
        }

        clientsNewDownstreamCallback(streamId, memberId, streamType, stream);
    }
    fullNewUpstreamCallback(self, clientsNewUpstreamCallback, streamId, memberId, streamType, stream) {

        // TRANSCRIPTION CODE - if new upstream is camera or microphone, add to the active recorder
        if (stream && (streamType==="camera" || streamType==="microphone")) {
            try {
                const recorder=self.#mdoRecorder;
                const oneAudioTrack=stream.getAudioTracks()[0];
                if (oneAudioTrack && recorder!=null) {recorder.addTrack(oneAudioTrack);}
            } catch(e) {console.error("fullNewUpstreamCallback: Error adding audio track to recorder:"+e);}
        }

        clientsNewUpstreamCallback(streamId, memberId, streamType, stream);
    }
    fullExitCallback(self, clientsExitCallback, json) {
        // TRANSCRIPTION CODE - recording lifecycle is driven by the server baton, not local exit
        clientsExitCallback(json);
    }

/*

    // functions NOT YET IMPLEMENTED
    inChannelExitChannel() {}, // exit channel
    toChannelCreateMember(userId) {}, // add member with access to channel
    toChannelInviteMember(memberId) {}, // send an invite email/notification to user
    toChannelRemoveMember(memberId) {}, // remove member from channel
    toChannelChangeParams(configParams) {}, // change channel parameters
    deleteChannelFromServer() {}, // delete channel from server
    enterChannel(status, presentingAs) {}, // enter visage=default and status=active
    
    // callback function setters NOT-implemented yet
    setOnChangedChannelStatus(callback) {#streams.setOnChangedChannelStatus(callback);}, // callback receives (channelId, "active"/"inactive"")
    setOnNewMember(callback) {#streams.setOnNewMember(callback);}, // callback receives (json:{channelId, memberJson})
    setOnChangedMemberStatus(callback) {#streams.setOnChangedMemberStatus(callback);}, // callback receives (channelId, memberId, newStatus)
    setOnChangedDownAudioState(callback) {#streams.setOnChangedDownAudioState(callback);}, // callback receives (json:{channelId, memberId, type, streamId, newState})
    setOnChannelMessageToMember(callback) {#streams.setOnChannelMessageToMember(callback);}, // callback receives (channelId, memberId, messageJson)
    setOnNewChannel(callback) {#streams.setOnNewChannel(callback);},  // callback receives (channelId, channelJson)

*/

}

export default ChannelComms;